import React, { useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listInvoicesBetween } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `fee-report-all-class-date-to-date` ("Fee
 * Collection Date Wise") — one row per date, one column per fee head,
 * institute-wide total collected on that date for that fee head. The old
 * app's own endpoint returns exactly this pivot pre-computed server-side
 * (up to 27 fixed fee-head slots); here it's built client-side from the
 * already-exposed `GET /api/fee-invoices/range` (each invoice's real
 * per-line fee-head amounts) — no new backend needed, matching
 * `FeeReportService`'s own documented "most fee-report variants are pure
 * frontend groupings" design.
 *
 * Confirmed the real computation directly (`FeeReportService
 * .classWiseFeeHeadCollectionDetails` / `findClassWiseFeeHeadCollectionDetails`):
 * sums `det.paid_amount` grouped by `(payment_date, fee_head)` — matches
 * summing each line's own `paidAmount` here. One real gap, fixed while
 * sweeping Fee Management: the real app's own `getDateListBetweenTwoDates`
 * fills in every calendar day in the range as its own row (zero-filled if
 * nothing was collected that day); this page originally only rendered dates
 * that actually had a collection, silently dropping every all-zero day.
 */
export default function FeeCollectionDateWisePage() {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [searchedRange, setSearchedRange] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      const data = await listInvoicesBetween(fromDate, toDate);
      setInvoices(data);
      setSearchedRange({ fromDate, toDate });
    } catch (err) {
      setError("Failed to load collection data.");
    } finally {
      setLoading(false);
    }
  };

  const { feeHeadNames, rows, grandTotals } = useMemo(() => {
    if (!invoices) return { feeHeadNames: [], rows: [], grandTotals: {} };

    const headSet = new Set();
    const byDate = new Map();

    invoices.forEach((inv) => {
      const date = inv.paymentDate;
      if (!date) return;
      (inv.lines || []).forEach((line) => {
        if (!line.paidAmount) return;
        headSet.add(line.feeHeadName);
        if (!byDate.has(date)) byDate.set(date, {});
        const dateRow = byDate.get(date);
        dateRow[line.feeHeadName] = (dateRow[line.feeHeadName] || 0) + line.paidAmount;
      });
    });

    const names = Array.from(headSet).sort();

    // Real old app's own getDateListBetweenTwoDates fills every calendar day
    // in the range, not just days that had a collection (zero-filled). Uses
    // the range actually searched, not the (possibly since-edited) date
    // fields, so it can't drift out of sync with the fetched invoices.
    const toDateString = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const allDates = [];
    if (searchedRange) {
      // toISOString() would convert to UTC first, silently shifting every date
      // by a day in a timezone ahead of UTC — build the string from local
      // date parts instead.
      for (
        let d = new Date(searchedRange.fromDate + "T00:00:00");
        d <= new Date(searchedRange.toDate + "T00:00:00");
        d.setDate(d.getDate() + 1)
      ) {
        allDates.push(toDateString(d));
      }
    }
    const dataRows = allDates.map((date) => ({ date, amounts: byDate.get(date) || {} }));

    const totals = {};
    names.forEach((name) => {
      totals[name] = dataRows.reduce((sum, r) => sum + (r.amounts[name] || 0), 0);
    });

    return { feeHeadNames: names, rows: dataRows, grandTotals: totals };
  }, [invoices, searchedRange]);

  const rowTotal = (amounts) => Object.values(amounts).reduce((sum, v) => sum + v, 0);
  const grandTotalSum = Object.values(grandTotals).reduce((sum, v) => sum + v, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Collection Date Wise</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        invoices && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {feeHeadNames.map((name) => (
                    <th key={name}>{name}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.date}>
                    <td>{r.date}</td>
                    {feeHeadNames.map((name) => (
                      <td key={name}>{(r.amounts[name] || 0).toFixed(2)}</td>
                    ))}
                    <td>{rowTotal(r.amounts).toFixed(2)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={feeHeadNames.length + 2}>No collection found for this range.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 800 }}>
                  <td>Grand Total</td>
                  {feeHeadNames.map((name) => (
                    <td key={name}>{grandTotals[name].toFixed(2)}</td>
                  ))}
                  <td>{grandTotalSum.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
